import { useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Download,
  FileSpreadsheet,
  MoreHorizontal,
  Pencil,
  Send,
  Trash2,
  X,
} from 'lucide-react'
import { Segmented } from './Segmented'
import { AnimatedNumber } from './AnimatedNumber'
import { locale, t } from '../i18n'
import type {
  BomSummary,
  ProjectMeta,
  QuoteInputs,
  QuoteMode,
  QuoteResult,
  RenderResult,
} from '../types'

interface Props {
  meta: ProjectMeta | null
  render: RenderResult | null
  mode: QuoteMode
  onMode: (mode: QuoteMode) => void
  inputs: QuoteInputs
  onInputs: (patch: Partial<QuoteInputs>) => void
  bom: BomSummary | null
  onBomFile: (file: File) => void
  onBomClear: () => void
  quote: QuoteResult
  onRename: () => void
  onDelete: () => void
  onDownload: () => void
  onSubmit: () => void
}

const LAYERS = [1, 2, 4, 6, 8, 10, 12]
const THICKNESS = [0.6, 0.8, 1.0, 1.2, 1.6, 2.0, 2.4, 3.0]
const FINISHES = ['hasl', 'hasl_lf', 'osp', 'enig', 'imm_silver', 'imm_tin', 'hard_gold']
const COPPER = [0.5, 1, 2, 3]
const MASKS = ['green', 'black', 'matte_black', 'blue', 'red', 'white']
const SILK = ['white', 'black', 'none']
const TRACE = [6, 5, 4, 3, 2]
const TESTING = ['e_test', 'flying_probe', 'fixture']

function money(value: number, digits = 2): string {
  return value.toLocaleString(locale(), { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="field-label">
      <span>{label}</span>
      {children}
    </label>
  )
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (value: boolean) => void; label: string }) {
  return (
    <label className="toggle-row">
      <span>{label}</span>
      <button
        className={`switch ${checked ? 'is-on' : ''}`}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
      >
        <span />
      </button>
    </label>
  )
}

export function Inspector({
  meta,
  render,
  mode,
  onMode,
  inputs,
  onInputs,
  bom,
  onBomFile,
  onBomClear,
  quote,
  onRename,
  onDelete,
  onDownload,
  onSubmit,
}: Props) {
  const [advanced, setAdvanced] = useState(false)
  const [breakdown, setBreakdown] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const [bomDrag, setBomDrag] = useState(false)

  const leadOptions = mode === 'pcb' ? ['standard', 'express', 'rush', '24h'] : ['standard', 'express', 'rush']
  const passed = render ? render.checks.filter((check) => check.ok).length : 0
  const missing = render ? render.checks.length - passed : 0

  return (
    <aside className="inspector" aria-label={t('quote.title')}>
      <header className="inspector-head">
        <div className="inspector-title">
          <span className="eyebrow">{mode === 'pcb' ? 'PCB' : 'PCBA'}</span>
          <strong>{t('quote.title')}</strong>
        </div>
        <div className="inspector-head-actions">
          <div className="inspector-menu-wrap">
            <button
              className="icon-btn"
              type="button"
              aria-label={t('project.more')}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((value) => !value)}
            >
              <MoreHorizontal size={16} />
            </button>
            {menuOpen ? (
              <>
                <button className="menu-scrim" type="button" aria-label={t('common.close')} onClick={() => setMenuOpen(false)} />
                <div className="project-menu inspector-menu" role="menu">
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onRename() }}>
                    <Pencil size={13} /> {t('project.rename')}
                  </button>
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onDownload() }}>
                    <Download size={13} /> {t('project.download')}
                  </button>
                  <button className="is-danger" type="button" role="menuitem" onClick={() => { setMenuOpen(false); onDelete() }}>
                    <Trash2 size={13} /> {t('project.delete')}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </header>

      <div className="inspector-scroll">
        <Segmented
          layoutId="mode-indicator"
          label={t('quote.title')}
          value={mode}
          onChange={onMode}
          className="mode-switch"
          options={[
            { value: 'pcb', label: t('quote.mode.pcb') },
            { value: 'pcba', label: t('quote.mode.pcba') },
          ]}
        />

        <section className="stats-block">
          <div className="stat">
            <span>{t('stats.size')}</span>
            <strong>
              {render ? (
                <>
                  <AnimatedNumber value={render.widthMm} format={(n) => n.toFixed(1)} />
                  <em> × </em>
                  <AnimatedNumber value={render.heightMm} format={(n) => n.toFixed(1)} />
                  <small> mm</small>
                </>
              ) : '—'}
            </strong>
          </div>
          <div className="stat">
            <span>{t('stats.copper')}</span>
            <strong>{render ? <AnimatedNumber value={render.copperLayers} /> : '—'}<small> L</small></strong>
          </div>
          <div className="stat">
            <span>{t('stats.drills')}</span>
            <strong>{render ? <AnimatedNumber value={render.drillHits} format={(n) => Math.round(n).toLocaleString(locale())} /> : '—'}</strong>
          </div>
          <div className="stat">
            <span>{t('stats.files')}</span>
            <strong>{render ? render.fileCount : '—'}</strong>
          </div>
          <div className="stat">
            <span>{t('stats.minAperture')}</span>
            <strong>{render?.minApertureMm ? `${render.minApertureMm.toFixed(2)} mm` : '—'}</strong>
          </div>
          <div className="stat">
            <span>{t('checks.title')}</span>
            <strong className={missing > 0 ? 'is-warn' : ''}>
              {render ? (missing > 0 ? t('checks.missing', { n: missing }) : t('checks.ok', { n: passed })) : '—'}
            </strong>
          </div>
        </section>

        {render ? (
          <section className="checks-block" aria-label={t('checks.title')}>
            {render.checks.map((check) => (
              <span className={`check ${check.ok ? 'is-ok' : 'is-missing'}`} key={check.key}>
                {check.ok ? <Check size={11} /> : <AlertTriangle size={11} />}
                {t(`check.${check.key}`)}
              </span>
            ))}
            {render.panelDetected ? (
              <span className="check is-warn"><AlertTriangle size={11} />{t('canvas.panel')}</span>
            ) : null}
          </section>
        ) : null}

        <section className="quote-form" aria-label={t('quote.title')}>
          {mode === 'pcba' ? (
            <div
              className={`bom-block ${bomDrag ? 'is-drag' : ''} ${bom ? 'has-bom' : ''}`}
              onDragOver={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setBomDrag(true)
              }}
              onDragLeave={() => setBomDrag(false)}
              onDrop={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setBomDrag(false)
                const file = event.dataTransfer.files[0]
                if (file) onBomFile(file)
              }}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  if (file) onBomFile(file)
                  event.target.value = ''
                }}
              />
              {bom ? (
                <>
                  <div className="bom-title">
                    <FileSpreadsheet size={14} />
                    <strong title={bom.fileName}>{bom.fileName}</strong>
                    <button className="icon-btn tiny" type="button" onClick={onBomClear} aria-label={t('quote.bomRemove')}>
                      <X size={13} />
                    </button>
                  </div>
                  <div className="bom-stats">
                    <span><strong>{bom.uniqueParts}</strong>{t('quote.parts')}</span>
                    <span><strong>{bom.placements}</strong>{t('quote.placements')}</span>
                    <span><strong>{bom.componentCost !== null ? `$${money(bom.componentCost)}` : '—'}</strong>{t('quote.components')}</span>
                  </div>
                </>
              ) : (
                <button className="bom-empty" type="button" onClick={() => fileRef.current?.click()}>
                  <FileSpreadsheet size={15} />
                  <span>
                    <strong>{t('quote.bomDrop')}</strong>
                    <small>{t('quote.bomHint')}</small>
                  </span>
                </button>
              )}
            </div>
          ) : null}

          <div className="field-grid">
            <Field label={t('quote.qty')}>
              <div className="stepper">
                <button type="button" onClick={() => onInputs({ qty: Math.max(1, inputs.qty - 10) })} aria-label="−">−</button>
                <input
                  type="number"
                  min={1}
                  max={100000}
                  value={inputs.qty}
                  onChange={(event) => onInputs({ qty: Math.max(1, Math.min(100000, Number(event.target.value) || 1)) })}
                  aria-label={t('quote.qty')}
                />
                <button type="button" onClick={() => onInputs({ qty: Math.min(100000, inputs.qty + 10) })} aria-label="+">+</button>
              </div>
            </Field>

            <Field label={t('quote.layers')}>
              <select className="field" value={inputs.layers} onChange={(event) => onInputs({ layers: Number(event.target.value) })}>
                {LAYERS.map((value) => <option value={value} key={value}>{value} L</option>)}
              </select>
            </Field>

            <Field label={t('quote.thickness')}>
              <select className="field" value={inputs.thickness} onChange={(event) => onInputs({ thickness: Number(event.target.value) })}>
                {THICKNESS.map((value) => <option value={value} key={value}>{value} mm</option>)}
              </select>
            </Field>

            <Field label={t('quote.finish')}>
              <select className="field" value={inputs.finish} onChange={(event) => onInputs({ finish: event.target.value })}>
                {FINISHES.map((value) => <option value={value} key={value}>{t(`finish.${value}`)}</option>)}
              </select>
            </Field>

            <Field label={t('quote.lead')}>
              <select className="field" value={inputs.lead} onChange={(event) => onInputs({ lead: event.target.value })}>
                {leadOptions.map((value) => <option value={value} key={value}>{t(`lead.${mode}.${value}`)}</option>)}
              </select>
            </Field>
          </div>

          <button className={`advanced-toggle ${advanced ? 'is-open' : ''}`} type="button" onClick={() => setAdvanced((value) => !value)}>
            {t('quote.advanced')}
            <ChevronDown size={13} />
          </button>

          <AnimatePresence initial={false}>
            {advanced ? (
              <motion.div
                className="advanced"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.16 }}
              >
                <div className="field-grid">
                  <Field label={t('quote.copper')}>
                    <select className="field" value={inputs.copper} onChange={(event) => onInputs({ copper: Number(event.target.value) })}>
                      {COPPER.map((value) => <option value={value} key={value}>{value} oz</option>)}
                    </select>
                  </Field>
                  <Field label={t('quote.mask')}>
                    <select className="field" value={inputs.mask} onChange={(event) => onInputs({ mask: event.target.value })}>
                      {MASKS.map((value) => <option value={value} key={value}>{t(`mask.${value}`)}</option>)}
                    </select>
                  </Field>
                  <Field label={t('quote.silk')}>
                    <select className="field" value={inputs.silkscreen} onChange={(event) => onInputs({ silkscreen: event.target.value })}>
                      {SILK.map((value) => <option value={value} key={value}>{t(`silkscreen.${value}`)}</option>)}
                    </select>
                  </Field>
                  <Field label={t('quote.trace')}>
                    <select className="field" value={inputs.trace} onChange={(event) => onInputs({ trace: Number(event.target.value) })}>
                      {TRACE.map((value) => <option value={value} key={value}>{t(`trace.${value}`)}</option>)}
                    </select>
                  </Field>
                  <Field label={t('quote.testing')}>
                    <select className="field" value={inputs.testing} onChange={(event) => onInputs({ testing: event.target.value })}>
                      {TESTING.map((value) => <option value={value} key={value}>{t(`testing.${value}`)}</option>)}
                    </select>
                  </Field>
                  {mode === 'pcba' ? (
                    <>
                      <Field label={t('quote.sides')}>
                        <select className="field" value={inputs.sides} onChange={(event) => onInputs({ sides: event.target.value as QuoteInputs['sides'] })}>
                          <option value="top">{t('sides.top')}</option>
                          <option value="both">{t('sides.both')}</option>
                        </select>
                      </Field>
                      <Field label={t('quote.technology')}>
                        <select className="field" value={inputs.technology} onChange={(event) => onInputs({ technology: event.target.value as QuoteInputs['technology'] })}>
                          {(['smt', 'tht', 'mixed'] as const).map((value) => <option value={value} key={value}>{t(`tech.${value}`)}</option>)}
                        </select>
                      </Field>
                      <Field label={t('quote.complexity')}>
                        <select className="field" value={inputs.complexity} onChange={(event) => onInputs({ complexity: event.target.value as QuoteInputs['complexity'] })}>
                          {(['standard', 'fine_pitch', 'bga'] as const).map((value) => <option value={value} key={value}>{t(`complexity.${value}`)}</option>)}
                        </select>
                      </Field>
                      <Field label={t('quote.assemblyTest')}>
                        <select className="field" value={inputs.assemblyTest} onChange={(event) => onInputs({ assemblyTest: event.target.value as QuoteInputs['assemblyTest'] })}>
                          {(['aoi', 'xray', 'functional'] as const).map((value) => <option value={value} key={value}>{t(`test.${value}`)}</option>)}
                        </select>
                      </Field>
                      <Field label={t('quote.coating')}>
                        <select className="field" value={inputs.coating} onChange={(event) => onInputs({ coating: event.target.value as QuoteInputs['coating'] })}>
                          {(['none', 'acrylic', 'silicone'] as const).map((value) => <option value={value} key={value}>{t(`coating.${value}`)}</option>)}
                        </select>
                      </Field>
                      <Field label={t('quote.parts')}>
                        <input className="field" type="number" min={0} value={inputs.uniqueParts} onChange={(event) => onInputs({ uniqueParts: Math.max(0, Number(event.target.value) || 0) })} />
                      </Field>
                      <Field label={t('quote.placements')}>
                        <input className="field" type="number" min={0} value={inputs.placements} onChange={(event) => onInputs({ placements: Math.max(0, Number(event.target.value) || 0) })} />
                      </Field>
                    </>
                  ) : null}
                </div>
                <div className="toggle-grid">
                  <Toggle checked={inputs.impedance} onChange={(value) => onInputs({ impedance: value })} label={t('quote.impedance')} />
                  <Toggle checked={inputs.castellation} onChange={(value) => onInputs({ castellation: value })} label={t('quote.castellation')} />
                  {mode === 'pcba' ? (
                    <>
                      <Toggle checked={inputs.includePcb} onChange={(value) => onInputs({ includePcb: value })} label={t('quote.includePcb')} />
                      <Toggle checked={inputs.boxBuild} onChange={(value) => onInputs({ boxBuild: value })} label={t('quote.boxBuild')} />
                    </>
                  ) : null}
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>

        </section>
      </div>

      <footer className="quote-total">
        <div className="total-line">
          <span>{t('quote.total')}</span>
          <strong>
            <em>$</em>
            <AnimatedNumber value={quote.total} format={(n) => money(n, n >= 1000 ? 0 : 2)} />
          </strong>
        </div>
        <div className="total-meta">
          <span>${money(quote.unit)} {t('quote.perPiece')}</span>
          <span>{inputs.qty} {t('quote.pcs')}</span>
          <span>{quote.leadTime}</span>
        </div>

        <button className="breakdown-toggle" type="button" onClick={() => setBreakdown((value) => !value)} aria-expanded={breakdown}>
          {breakdown ? t('quote.hideBreakdown') : t('quote.breakdown')}
          <ChevronDown size={12} className={breakdown ? 'is-open' : ''} />
        </button>
        <AnimatePresence initial={false}>
          {breakdown ? (
            <motion.ul
              className="breakdown"
              initial={{ opacity: 0, y: -3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -3 }}
              transition={{ duration: 0.15 }}
            >
              {quote.lines.map((line) => (
                <li className={line.emphasis ? 'is-total' : ''} key={line.label}>
                  <span>{line.label}</span>
                  <strong>{line.value}</strong>
                </li>
              ))}
            </motion.ul>
          ) : null}
        </AnimatePresence>

        {quote.warnings.length > 0 ? (
          <p className="quote-warning">
            <AlertTriangle size={11} />
            {quote.warnings[0]}
          </p>
        ) : null}

        <button className="btn btn-primary submit-btn" type="button" onClick={onSubmit}>
          <Send size={14} />
          {t('submit.open')}
        </button>
        <p className="disclaimer">{t('quote.disclaimer')}</p>
      </footer>
    </aside>
  )
}
